import { useEffect, useState } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import Stepper from '@/components/Stepper';
import ToolBar from '@/components/ToolBar';
import AlertBox from '@/components/AlertBox';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { displayError, normalizeAxiosError } from '@/lib/error';
import { isAdminOrSupervisor } from '@/services/userService';
import { useHIWorkOrderByNumber } from '@/services/homeInspectionService';
import { createAttempt } from '@/services/homeInspectionAttemptService';
import { useUserStore } from '@/stores/user';
import { format, parse } from 'date-fns';
import { getPhoneNumberValue } from '@/components/FormFieldPhone';
import type { ExtendedUserResponseSchema } from '@/types/user.types';
import type { PasswordChallengeResponse } from '@brainvaulttech/dgr-react-sdk';
import {
  HI_ATTEMPT_STEP_SCHEMAS,
  type HiAttemptFormValues,
  type HiAttemptCreatePayload,
} from './hiAttempt.schema';
import { HiAttemptStep1OwnerInfo } from './steps/HiAttemptStep1OwnerInfo';
import { HiAttemptStep2TenantInfo } from './steps/HiAttemptStep2TenantInfo';
import { HiAttemptStep3PropertyInfo, type PreviewEntry } from './steps/HiAttemptStep3PropertyInfo';
import { HiAttemptStep4Comments } from './steps/HiAttemptStep4Comments';

const STEPS = [
  { title: 'Owner Information', isCompleted: false },
  { title: 'Tenant Information', isCompleted: false },
  { title: 'Property Information', isCompleted: false },
  { title: 'Comments', isCompleted: false },
];

type LoggedInUser = (PasswordChallengeResponse & ExtendedUserResponseSchema) | null;

const canCreateAttempt = (user: LoggedInUser): boolean => {
  if (!user) return false;
  if (isAdminOrSupervisor(user)) return true;
  return user.teams.some((t) => t.key === 'SERVICE');
};

export const HiAttemptCreate = () => {
  const { id: workOrderNumber } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const loggedInUser = useUserStore((state) => state.user);

  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState(STEPS);
  const [currentSchema, setCurrentSchema] = useState<(typeof HI_ATTEMPT_STEP_SCHEMAS)[number]>(
    HI_ATTEMPT_STEP_SCHEMAS[0]
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAlertOpen, setSubmitAlertOpen] = useState(false);
  const [previews, setPreviews] = useState<PreviewEntry[]>([]);

  const { data: workOrder, isFetching, isError } = useHIWorkOrderByNumber(workOrderNumber ?? null);

  const form = useForm<HiAttemptFormValues>({
    resolver: zodResolver(currentSchema),
    mode: 'all',
    defaultValues: {
      mediaIds: [],
      tenants: [],
    },
  });

  useEffect(() => {
    setCurrentSchema(HI_ATTEMPT_STEP_SCHEMAS[activeStep]);
    setTimeout(async () => {
      await form.trigger();
      form.clearErrors();
    }, 50);
    scrollToTop();
  }, [activeStep]);

  const scrollToTop = () => {
    const el = document.getElementById('toolbar');
    if (el) el.scrollIntoView({ block: 'end', behavior: 'smooth' });
  };

  const nextStep = async () => {
    const isValid = await form.trigger();
    if (isValid) {
      setSteps((prev) => prev.map((s, i) => (i === activeStep ? { ...s, isCompleted: true } : s)));
      setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
    }
  };

  const prevStep = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const goToStep = async (step: number) => {
    const isValid = await form.trigger();
    if (isValid) {
      if (steps[step].isCompleted || step <= activeStep) {
        setActiveStep(step);
      }
    }
  };

  const saveHandler = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;

    if (!workOrder) return;
    const workOrderId = workOrder.workOrderId;

    setIsSubmitting(true);
    try {
      const values = form.getValues();

      const parsedTime = parse(values.timeOfAttempt, 'hh:mm a', new Date());
      const payload: HiAttemptCreatePayload = {
        dateOfAttempt: values.dateOfAttempt instanceof Date ? values.dateOfAttempt.toISOString() : values.dateOfAttempt,
        timeOfAttempt: format(parsedTime, 'HH:mm'),
        ownerAddressLine1: values.ownerAddressLine1 ?? null,
        ownerAddressLine2: values.ownerAddressLine2 ?? null,
        ownerCity: values.ownerCity ?? null,
        ownerState: values.ownerState ?? null,
        ownerZipCode: values.ownerZipCode ?? null,
        ownerPhone: getPhoneNumberValue(values.ownerPhone) ?? null,
        maritalStatus: values.maritalStatus ?? null,
        spouseName: values.maritalStatus === 'MARRIED' ? (values.spouseName ?? null) : null,
        ownerOccupied: values.ownerOccupied ?? null,
        vacant: values.vacant ?? null,
        bothOwnersResideTogether: values.bothOwnersResideTogether ?? null,
        coOwnerName: values.coOwnerName ?? null,
        coOwnerAddressLine1: values.coOwnerAddressLine1 ?? null,
        coOwnerAddressLine2: values.coOwnerAddressLine2 ?? null,
        coOwnerCity: values.coOwnerCity ?? null,
        coOwnerState: values.coOwnerState ?? null,
        coOwnerZipCode: values.coOwnerZipCode ?? null,
        defendantInArmedForces: values.defendantInArmedForces ?? null,
        tenantOccupied: values.tenantOccupied ?? null,
        tenants: values.tenants?.length
          ? values.tenants.map((t) => ({ name: t.name, floor: t.floor ?? null }))
          : null,
        relative: values.tenants?.[0]?.relative ?? null,
        lease: values.tenants?.[0]?.lease ?? null,
        rentAmount: values.tenants?.[0]?.rentAmount != null ? Number(values.tenants[0].rentAmount) : null,
        houseExterior: values.houseExterior,
        houseExteriorOther: values.houseExterior === 'OTHER' ? (values.houseExteriorOther ?? undefined) : undefined,
        houseType: values.houseType,
        numberOfUnits: values.numberOfUnits != null ? Number(values.numberOfUnits) : 1,
        garage: values.garage,
        sale: values.sale,
        saleBy: values.sale === 'YES' ? (values.saleBy ?? undefined) : undefined,
        salePhone: values.sale === 'YES' ? (getPhoneNumberValue(values.salePhone) ?? undefined) : undefined,
        comments: values.comments ?? undefined,
        mediaIds: values.mediaIds ?? [],
      };

      await createAttempt(workOrderId, payload);

      void queryClient.invalidateQueries({ queryKey: ['hi-work-order-detail', workOrderId] });

      toast({
        variant: 'default',
        description: 'Home Inspection attempt created successfully.',
        duration: 5000,
      });

      navigate(`/home-inspection`);
    } catch (err) {
      displayError(normalizeAxiosError(err), 5000);
    } finally {
      setIsSubmitting(false);
      setSubmitAlertOpen(false);
    }
  };

  if (!canCreateAttempt(loggedInUser)) {
    return <Navigate to="/not-found" />;
  }

  if (isFetching) {
    return (
      <div className="flex items-center justify-center h-64 text-placeholder text-sm">
        Loading work order…
      </div>
    );
  }

  if (isError || !workOrder) {
    return (
      <div className="flex items-center justify-center h-64 text-error text-sm">
        Failed to load work order. Please go back and try again.
      </div>
    );
  }

  return (
    <div className="w-full scrollbar-hide flex flex-col">
      <div className="w-full">
        <ToolBar title={`Add Attempt — W/O #${workOrder.workOrderNumber}`} />
        <div className="mt-3 flex justify-center pl-24">
          <Stepper steps={steps} activeStep={activeStep} goToStep={goToStep} />
        </div>

        <FormProvider {...form}>
          {activeStep === 0 && <HiAttemptStep1OwnerInfo workOrder={workOrder} />}
          {activeStep === 1 && <HiAttemptStep2TenantInfo />}
          {activeStep === 2 && (
            <HiAttemptStep3PropertyInfo previews={previews} setPreviews={setPreviews} />
          )}
          {activeStep === 3 && <HiAttemptStep4Comments workOrder={workOrder} />}
        </FormProvider>

        <div className="flex justify-end mt-4 mb-8 w-full gap-2">
          {activeStep > 0 && (
            <Button
              variant="outline"
              className="text-primary py-2 px-4 rounded-md border-primary flex justify-center w-[125px] h-[48px] hover:bg-[#EDF5FF]"
              onClick={prevStep}
            >
              Back
            </Button>
          )}

          {activeStep !== STEPS.length - 1 && (
            <Button
              className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white disabled:bg-primary"
              onClick={nextStep}
              disabled={!form.formState.isValid}
            >
              Next
            </Button>
          )}

          {activeStep === STEPS.length - 1 && (
            <AlertBox
              open={submitAlertOpen}
              triggerButton={
                <Button
                  variant="outline"
                  className="text-[#fff] py-2 px-4 rounded-md bg-primary w-[125px] h-[48px] border-primary flex justify-center hover:bg-[#12375C] hover:text-white"
                  onClick={() => setSubmitAlertOpen(true)}
                  disabled={!form.formState.isValid || isSubmitting}
                >
                  Save
                </Button>
              }
              title="Create Home Inspection Attempt?"
              description="This will submit the attempt record to the work order."
              cancelBtnLabel="Cancel"
              confirmBtnLabel="Save"
              cancelBtnClick={() => setSubmitAlertOpen(false)}
              isLoading={isSubmitting}
              confirmBtnClick={saveHandler}
            />
          )}
        </div>
      </div>
    </div>
  );
};
